<template>
  <div class="form-group row centix-vue-priceinput" :class="{ 'disabled' : !isEnabled }" v-show="isVisible">
    <form-label :label="label" :info="info" labelCssClass="col-form-label col-md-4" :infoIconWrapper="infoIconWrapper"/>
    <div class="col-8" :class="{'disabled': this.isEnabled === false}">
      <div class="row">
        <div class="col position-relative">
          <a class="btn btn-sm filter-button currencies-button" href="javascript:void(0)" @click="showCurrenciesList">
            <strong v-if="selectedCurrency && selectedCurrency.Symbol">{{ selectedCurrency.Symbol }}</strong>
            <strong v-else class="placeholder-text">$</strong>
            <p v-if="selectedCurrency">{{ selectedCurrency.ID.slice(0, 3) }}</p>
            <svg viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4 L6 8 L9 4" stroke="#333" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" :stroke-opacity="currencyEnabled ? 1 : 0"/>
            </svg>
          </a>
          <transition name="fade">
            <template v-if="showCurrencies">
              <div class="info-box">
                <p 
                  v-for="item in currencies" 
                  :key="item.AutoID"
                  :class="{ 'active-option': item.AutoID == selectedCurrency.AutoID }" 
                  @click="selectCurrency(item.AutoID, true)" >
                  <span v-if="item.Symbol" class="symbol">{{ item.Symbol }}</span>
                  <span v-else class="symbol"></span>
                  <span>{{ item.ID  + (item.Descr ? ` - ${item.Descr}` : '') }}</span>
                </p>
                <div class="popupmask" @click.self="showCurrencies = false">
                </div>
              </div>
            </template>
          </transition>
          <input
            type="number"
            :step="step"
            pattern="[0-9]"
            class="form-control input-item"
            :min="min"
            :max="max"
            :class="{ filled : hasValue, required : showRequiredClass }"
            v-model.number="innerValue"
            @input="onChange"
            @keyup.esc="$event.target.blur()"
            @blur="onBlur"
            @keyup.enter="onReturn"
            :placeholder="placeholder"
            :disabled="!isEnabled">
        </div>
      </div>
    </div>
    <div class="model-state-error col-12">
      {{ validationError }}
    </div>
  </div>
</template>

<script>
import FormMixin from '@/components/Form/formitem.js';
import BaseApiController from 'controller/baseresourcecontroller';

const currencyController = new BaseApiController('mvc/Currency');

export default {
  mixins: [FormMixin],
  props: {
    currencyConfig: {
      type: Object,
      required: true
    },
    currency: Number,
    min: Number,
    max: Number,
    step: {
      type: Number,
      default: 1
    },
  },
  data () {
    return {
      innerValue: '',
      innerCurrency: '',
      showCurrencies: false,
      currencies: [],
      selectedCurrency: null
    };

  },
  async mounted () {
    this.innerValue = this.value;
    this.innerCurrency = this.currency;
    this.currencies = await currencyController.Get('GetCurrencies');
    this.selectCurrency(this.currencyConfig.value, false);
  },
  methods: {
    onChange() {
      //getNumberOrNull returns NULL if this.innerValue is not a number
      let val = this.getNumberOrNull(this.innerValue);
      
      //only checkAndEmit if val is a number      
      if (val !== null) {
        this.checkAndEmit(val);
      }
    },
    onBlur() {
      this.convert();
      this.$emit('on-blur', this);
    },
    convert () {
      let val = this.getNumberOrNull(this.innerValue);
      
      if (typeof val === 'number' && val !== 0 && this.step && this.step !== 1) {
        const decimal = this.getDecimal(this.step);
        const fixed = val.toFixed(decimal); 
        val = Number(fixed); 
        this.innerValue = fixed; 
      }
      this.checkAndEmit(val);
    },
    checkAndEmit (value) {
      if (this.value !== value) {
        this.$emit('change', value);
        this.emitFormChange();
      }
    },
    getNumberOrNull(value) {
      let val = parseFloat(value);
      return (!Number.isNaN(val)) ? val : null;
    },
    selectCurrency(autoID, emitEvent) {
      this.selectedCurrency = this.currencies.find(c => c.AutoID === autoID);
      this.innerCurrency = autoID;
      this.showCurrencies = false;

      if (emitEvent){
        this.$emit('update:currency', autoID);
        this.emitFormChange();
      }
    },
    showCurrenciesList() {
      // alleen tonen als enabled
      if (this.currencyEnabled) {
        this.showCurrencies = true;         
      }

      // focus moet op de input blijven
      this.$nextTick(() => { 
        const element = this.$el.querySelector('.input-item');
        if (element) {
          element.focus();
        }
      });
    },
  },
  watch: {
    value () {
      this.innerValue = (this.value === null) ? ' ' : this.value; // fix for NaN. Otherwise the input is not reset.
    },
    currency (newCurrency) {
      this.selectCurrency(newCurrency, false);
    }
  },
  computed: {
    hasValue () {
      return this.innerValue != null && (this.innerValue.toString().trim().length > 0);
    },
    showRequiredClass () {
      if (this.required && !this.hasValue) {
        return true;
      } else {
        return false;
      }
    },
    currencyEnabled () {
      return this.currencyConfig && this.currencyConfig.enabled && this.isEnabled;
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'styles/settings.scss';
.centix-vue-priceinput {
  &:not(.disabled) {
    .input-item {
      padding: 6px;

        &.required {
          border: 1px solid red;
        }

        &.filled {
          border: 1px solid #80bdff;
        }
    }
  }

  .placeholder-text {
    color: transparent;
    user-select: none;
    -webkit-user-select: none;
  }

  .input-item {
    padding-left: 60px !important;
    text-align: right;
    max-width: calc(9em + 23px);
  }

  .currencies-button {
    position: absolute;
    inset-block: 1px;
    inset-inline-start: 16px;

    height: calc(100% - 2px);
    min-width: 55px;
    box-sizing: border-box;

    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 4px 7px 6px;

    background: #e9e9ed;
    border: none;
    border-right: 1px solid #A9A9A9;
    border-radius: 4px 0 0 4px;

    p {
      margin: 0;
      padding: 0;
    }

    svg { 
      flex-shrink: 0; 
      width: 10px; 
      height: 10px; 
    }

    &:focus { 
      outline: none; 
      box-shadow: none; 
    }
  }
  
  span.symbol {
    display: inline-block;
    width: 25px;
  }

  .info-box {
    position: absolute;
    z-index: 10;
    top: 100%;
    left: 15px;
    border: 1px solid rgba(0, 0, 0, 0.125);
    border-radius: 4px;
    padding: 6px;
    background: #f2f4f6;
    width: calc(100% - 30px);

    p {
      background-color: rgba(255, 255, 255, 0.95);
      margin: 0;
      padding: 0.5rem 2.5rem 0.5rem 1rem;
      cursor: pointer;
    }

    p:hover:not(.active-option) {
      background-color: $blue100;
    }

    .active-option {
      background-color: #007bff;
      color: white;
    }
  }

  .popupmask {
    top: 0;
    position: fixed;
    background: rgba(0,0,0,0);
    z-index: -1;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .model-state-error {
    color: $error;
  }
}
</style>